/**
 * CustomizationScene — Phaser Scene for character customization.
 *
 * Displays on first launch with two options:
 * - "Personalizar Personagem" → opens file picker for image upload
 * - "Jogar com Personagem Padrão" → proceeds to MapSelectScene with default character
 *
 * Upload flow:
 * 1. Open HTML file input (PNG only)
 * 2. Validate: 128×128 min, 512×512 max, PNG format, < 2MB
 * 3. Show preview with silhouette guide (head region = 40% of character height)
 * 4. On confirm: save base64 via ProgressTracker.saveCharacterImage()
 * 5. On failure: show friendly message and fallback to default
 *
 * Accessible via settings at any time.
 * All text in pt-PT.
 *
 * Requirements: 21.1, 21.2, 21.3, 21.4, 21.5, 21.8, 21.9, 21.10, 21.12
 */

import Phaser from 'phaser';
import type { ProgressTracker } from '../managers/ProgressTracker';

/** Scene data passed when starting CustomizationScene */
export interface CustomizationSceneData {
  progressTracker: ProgressTracker;
  /** If true, return to settings/MapSelectScene after customization */
  fromSettings?: boolean;
}

// --- Visual constants ---

const COLORS = {
  background: 0x1a1a2e,
  buttonPrimary: 0x4a90d9,
  buttonSecondary: 0x6c757d,
  buttonConfirm: 0x28a745,
  buttonCancel: 0xdc3545,
  buttonHover: 0x5aa0e9,
  text: '#ffffff',
  subText: '#ccccff',
  silhouetteOutline: 0xffd700,
  silhouetteFill: 0x333355,
  previewBorder: 0xffd700,
  errorText: '#ff6b6b',
};

const LAYOUT = {
  buttonWidth: 320,
  buttonHeight: 64,
  buttonRadius: 16,
  buttonSpacing: 30,
  previewSize: 200,
  silhouetteCharHeight: 280,
  headRatio: 0.4,
};

/** Image validation constraints */
const IMAGE_CONSTRAINTS = {
  minWidth: 128,
  minHeight: 128,
  maxWidth: 512,
  maxHeight: 512,
  maxFileSize: 2 * 1024 * 1024, // 2MB
  allowedMimeType: 'image/png',
};

export class CustomizationScene extends Phaser.Scene {
  private progressTracker!: ProgressTracker;
  private fromSettings = false;

  // UI state
  private mainContainer: Phaser.GameObjects.Container | null = null;
  private previewContainer: Phaser.GameObjects.Container | null = null;
  private messageText: Phaser.GameObjects.Text | null = null;
  private fileInput: HTMLInputElement | null = null;
  private uploadedImageData: string | null = null;

  constructor() {
    super({ key: 'CustomizationScene' });
  }

  init(data: CustomizationSceneData): void {
    this.progressTracker = data.progressTracker;
    this.fromSettings = data.fromSettings ?? false;
  }

  create(): void {
    this.uploadedImageData = null;

    const { width, height } = this.scale;

    // Background
    this.add.rectangle(width / 2, height / 2, width, height, COLORS.background).setDepth(0);

    // Show main selection screen
    this.showMainScreen();
  }

  shutdown(): void {
    this.cleanupFileInput();
  }

  // ─── Main Screen ──────────────────────────────────────────────────────────────

  /**
   * Shows the initial screen with two options:
   * - "Personalizar Personagem" (upload)
   * - "Jogar com Personagem Padrão" (skip)
   */
  private showMainScreen(): void {
    this.clearContainers();

    const { width, height } = this.scale;
    this.mainContainer = this.add.container(0, 0).setDepth(10);

    // Title
    const title = this.add
      .text(width / 2, height * 0.15, 'Personaliza o teu Personagem!', {
        fontFamily: 'Arial, sans-serif',
        fontSize: '32px',
        color: COLORS.text,
        fontStyle: 'bold',
        align: 'center',
      })
      .setOrigin(0.5, 0.5);
    this.mainContainer.add(title);

    // Subtitle
    const subtitle = this.add
      .text(
        width / 2,
        height * 0.22,
        'Podes usar uma imagem personalizada como rosto\ndo teu personagem na aventura!',
        {
          fontFamily: 'Arial, sans-serif',
          fontSize: '18px',
          color: COLORS.subText,
          align: 'center',
          lineSpacing: 6,
        }
      )
      .setOrigin(0.5, 0.5);
    this.mainContainer.add(subtitle);

    // Silhouette guide (visual guide showing where face appears)
    this.drawSilhouetteGuide(width / 2, height * 0.48);

    // Buttons
    const buttonsY = height * 0.78;

    this.createButton(
      width / 2,
      buttonsY,
      'Personalizar Personagem',
      COLORS.buttonPrimary,
      () => this.openFilePicker()
    );

    this.createButton(
      width / 2,
      buttonsY + LAYOUT.buttonHeight + LAYOUT.buttonSpacing,
      'Jogar com Personagem Padrão',
      COLORS.buttonSecondary,
      () => this.skipCustomization()
    );

    // Message area (for errors/feedback)
    this.messageText = this.add
      .text(width / 2, height * 0.93, '', {
        fontFamily: 'Arial, sans-serif',
        fontSize: '16px',
        color: COLORS.errorText,
        align: 'center',
        wordWrap: { width: width * 0.8 },
      })
      .setOrigin(0.5, 0.5);
    this.mainContainer.add(this.messageText);
  }

  // ─── Silhouette Guide ─────────────────────────────────────────────────────────

  /**
   * Draws a visual guide (silhouette outline) showing where the face appears
   * and at what size. The head region is 40% of total character height.
   * Requirement 21.10
   */
  private drawSilhouetteGuide(centerX: number, centerY: number): void {
    if (!this.mainContainer) return;

    const charHeight = LAYOUT.silhouetteCharHeight;
    const headHeight = charHeight * LAYOUT.headRatio;
    const headWidth = headHeight; // Square head region
    const bodyHeight = charHeight - headHeight;
    const bodyWidth = headWidth * 0.8;

    const topY = centerY - charHeight / 2;

    // Body silhouette (simple rounded rectangle)
    const bodyGraphics = this.add.graphics();
    bodyGraphics.fillStyle(COLORS.silhouetteFill, 0.6);
    bodyGraphics.fillRoundedRect(
      centerX - bodyWidth / 2,
      topY + headHeight,
      bodyWidth,
      bodyHeight,
      12
    );
    // Body outline
    bodyGraphics.lineStyle(2, COLORS.silhouetteOutline, 0.5);
    bodyGraphics.strokeRoundedRect(
      centerX - bodyWidth / 2,
      topY + headHeight,
      bodyWidth,
      bodyHeight,
      12
    );
    this.mainContainer.add(bodyGraphics);

    // Head region (highlighted area where face goes)
    const headGraphics = this.add.graphics();
    // Dashed outline effect for head region
    headGraphics.lineStyle(3, COLORS.silhouetteOutline, 1);
    headGraphics.strokeRoundedRect(
      centerX - headWidth / 2,
      topY,
      headWidth,
      headHeight,
      headWidth / 2 // Circular/oval shape
    );
    // Fill with semi-transparent to show it's the upload area
    headGraphics.fillStyle(COLORS.silhouetteOutline, 0.15);
    headGraphics.fillRoundedRect(
      centerX - headWidth / 2,
      topY,
      headWidth,
      headHeight,
      headWidth / 2
    );
    this.mainContainer.add(headGraphics);

    // Label for head region
    const headLabel = this.add
      .text(centerX, topY + headHeight / 2, '📷\nA tua foto\naparece aqui', {
        fontFamily: 'Arial, sans-serif',
        fontSize: '14px',
        color: COLORS.text,
        align: 'center',
        lineSpacing: 4,
      })
      .setOrigin(0.5, 0.5);
    this.mainContainer.add(headLabel);

    // Size annotation
    const sizeLabel = this.add
      .text(
        centerX + headWidth / 2 + 15,
        topY + headHeight / 2,
        `← 40% da\n   altura`,
        {
          fontFamily: 'Arial, sans-serif',
          fontSize: '12px',
          color: COLORS.subText,
          align: 'left',
          lineSpacing: 2,
        }
      )
      .setOrigin(0, 0.5);
    this.mainContainer.add(sizeLabel);

    // Arms (simple lines for silhouette)
    const armGraphics = this.add.graphics();
    armGraphics.lineStyle(2, COLORS.silhouetteFill, 0.6);
    // Left arm
    armGraphics.lineBetween(
      centerX - bodyWidth / 2,
      topY + headHeight + bodyHeight * 0.2,
      centerX - bodyWidth / 2 - 20,
      topY + headHeight + bodyHeight * 0.5
    );
    // Right arm
    armGraphics.lineBetween(
      centerX + bodyWidth / 2,
      topY + headHeight + bodyHeight * 0.2,
      centerX + bodyWidth / 2 + 20,
      topY + headHeight + bodyHeight * 0.5
    );
    this.mainContainer.add(armGraphics);
  }

  // ─── File Upload ──────────────────────────────────────────────────────────────

  /**
   * Opens the browser file picker for PNG image upload.
   * Creates a hidden HTML input element to trigger the native file dialog.
   */
  private openFilePicker(): void {
    this.cleanupFileInput();

    this.fileInput = document.createElement('input');
    this.fileInput.type = 'file';
    this.fileInput.accept = 'image/png';
    this.fileInput.style.display = 'none';
    document.body.appendChild(this.fileInput);

    this.fileInput.addEventListener('change', (event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file) {
        this.handleFileSelected(file);
      }
      this.cleanupFileInput();
    });

    this.fileInput.click();
  }

  /**
   * Handles a selected file: validates and processes the image.
   */
  private handleFileSelected(file: File): void {
    // Validate MIME type
    if (file.type !== IMAGE_CONSTRAINTS.allowedMimeType) {
      this.showError('A imagem deve ser no formato PNG.');
      return;
    }

    // Validate file size
    if (file.size > IMAGE_CONSTRAINTS.maxFileSize) {
      this.showError('A imagem é demasiado grande. O máximo é 2MB.');
      return;
    }

    // Read and validate dimensions
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      if (!dataUrl) {
        this.showFallbackError();
        return;
      }
      this.validateImageDimensions(dataUrl);
    };
    reader.onerror = () => {
      this.showFallbackError();
    };
    reader.readAsDataURL(file);
  }

  /**
   * Validates image dimensions (128×128 min, 512×512 max).
   */
  private validateImageDimensions(dataUrl: string): void {
    const img = new Image();
    img.onload = () => {
      const { width, height } = img;

      if (
        width < IMAGE_CONSTRAINTS.minWidth ||
        height < IMAGE_CONSTRAINTS.minHeight
      ) {
        this.showError(
          `A imagem é demasiado pequena. Mínimo: ${IMAGE_CONSTRAINTS.minWidth}×${IMAGE_CONSTRAINTS.minHeight} pixels.`
        );
        return;
      }

      if (
        width > IMAGE_CONSTRAINTS.maxWidth ||
        height > IMAGE_CONSTRAINTS.maxHeight
      ) {
        this.showError(
          `A imagem é demasiado grande. Máximo: ${IMAGE_CONSTRAINTS.maxWidth}×${IMAGE_CONSTRAINTS.maxHeight} pixels.`
        );
        return;
      }

      // Image is valid — crop/scale to head region and show preview
      this.processAndPreview(dataUrl, img);
    };
    img.onerror = () => {
      this.showFallbackError();
    };
    img.src = dataUrl;
  }

  /**
   * Processes the validated image: crops/scales to fit the head region
   * proportionally (maintaining aspect ratio) and shows a preview.
   * Requirement 21.5
   */
  private processAndPreview(dataUrl: string, img: HTMLImageElement): void {
    try {
      // Crop to square (center crop) for head region
      const size = Math.min(img.width, img.height);
      const sx = (img.width - size) / 2;
      const sy = (img.height - size) / 2;

      // Create canvas for cropping/scaling
      const canvas = document.createElement('canvas');
      const targetSize = 256; // Standard head texture size
      canvas.width = targetSize;
      canvas.height = targetSize;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        this.showFallbackError();
        return;
      }

      // Draw cropped and scaled image
      ctx.drawImage(img, sx, sy, size, size, 0, 0, targetSize, targetSize);

      // Get the processed base64 data
      const processedDataUrl = canvas.toDataURL('image/png');
      this.uploadedImageData = processedDataUrl;

      // Show preview screen
      this.showPreviewScreen(processedDataUrl);
    } catch {
      this.showFallbackError();
    }
  }

  // ─── Preview Screen ───────────────────────────────────────────────────────────

  /**
   * Shows the preview screen with the processed image on the silhouette
   * and confirm/cancel buttons.
   */
  private showPreviewScreen(imageDataUrl: string): void {
    this.clearContainers();

    const { width, height } = this.scale;
    this.previewContainer = this.add.container(0, 0).setDepth(10);

    // Title
    const title = this.add
      .text(width / 2, height * 0.08, 'Pré-visualização', {
        fontFamily: 'Arial, sans-serif',
        fontSize: '28px',
        color: COLORS.text,
        fontStyle: 'bold',
        align: 'center',
      })
      .setOrigin(0.5, 0.5);
    this.previewContainer.add(title);

    // Subtitle
    const subtitle = this.add
      .text(width / 2, height * 0.14, 'É assim que o teu personagem vai ficar!', {
        fontFamily: 'Arial, sans-serif',
        fontSize: '16px',
        color: COLORS.subText,
        align: 'center',
      })
      .setOrigin(0.5, 0.5);
    this.previewContainer.add(subtitle);

    // Character preview with uploaded head
    this.drawPreviewCharacter(width / 2, height * 0.45, imageDataUrl);

    // Confirm button
    this.createButton(
      width / 2,
      height * 0.75,
      'Confirmar ✓',
      COLORS.buttonConfirm,
      () => this.confirmCustomization()
    );

    // Cancel / try again button
    this.createButton(
      width / 2,
      height * 0.75 + LAYOUT.buttonHeight + LAYOUT.buttonSpacing,
      'Escolher outra imagem',
      COLORS.buttonSecondary,
      () => this.showMainScreen()
    );
  }

  /**
   * Draws the character preview with the uploaded image as the head.
   */
  private drawPreviewCharacter(
    centerX: number,
    centerY: number,
    imageDataUrl: string
  ): void {
    if (!this.previewContainer) return;

    const charHeight = LAYOUT.silhouetteCharHeight;
    const headHeight = charHeight * LAYOUT.headRatio;
    const headWidth = headHeight;
    const bodyHeight = charHeight - headHeight;
    const bodyWidth = headWidth * 0.8;
    const topY = centerY - charHeight / 2;

    // Body (filled silhouette)
    const bodyGraphics = this.add.graphics();
    bodyGraphics.fillStyle(COLORS.silhouetteFill, 0.8);
    bodyGraphics.fillRoundedRect(
      centerX - bodyWidth / 2,
      topY + headHeight,
      bodyWidth,
      bodyHeight,
      12
    );
    bodyGraphics.lineStyle(2, COLORS.silhouetteOutline, 0.6);
    bodyGraphics.strokeRoundedRect(
      centerX - bodyWidth / 2,
      topY + headHeight,
      bodyWidth,
      bodyHeight,
      12
    );
    this.previewContainer.add(bodyGraphics);

    // Arms
    const armGraphics = this.add.graphics();
    armGraphics.lineStyle(3, COLORS.silhouetteFill, 0.8);
    armGraphics.lineBetween(
      centerX - bodyWidth / 2,
      topY + headHeight + bodyHeight * 0.2,
      centerX - bodyWidth / 2 - 20,
      topY + headHeight + bodyHeight * 0.5
    );
    armGraphics.lineBetween(
      centerX + bodyWidth / 2,
      topY + headHeight + bodyHeight * 0.2,
      centerX + bodyWidth / 2 + 20,
      topY + headHeight + bodyHeight * 0.5
    );
    this.previewContainer.add(armGraphics);

    // Head image preview (loaded as Phaser texture)
    const textureKey = `custom-head-preview-${Date.now()}`;
    if (this.textures.exists(textureKey)) {
      this.textures.remove(textureKey);
    }

    // Load the image as a texture and display it
    const img = new Image();
    img.onload = () => {
      if (!this.previewContainer) return;

      try {
        this.textures.addImage(textureKey, img);
        const headSprite = this.add
          .image(centerX, topY + headHeight / 2, textureKey)
          .setDisplaySize(headWidth, headHeight);
        this.previewContainer.add(headSprite);

        // Border around head
        const borderGraphics = this.add.graphics();
        borderGraphics.lineStyle(3, COLORS.previewBorder, 1);
        borderGraphics.strokeRoundedRect(
          centerX - headWidth / 2,
          topY,
          headWidth,
          headHeight,
          headWidth / 2
        );
        this.previewContainer.add(borderGraphics);
      } catch {
        // If texture loading fails, show placeholder
        this.drawHeadPlaceholder(centerX, topY, headWidth, headHeight);
      }
    };
    img.onerror = () => {
      this.drawHeadPlaceholder(centerX, topY, headWidth, headHeight);
    };
    img.src = imageDataUrl;
  }

  /**
   * Draws a placeholder for the head if image loading fails in preview.
   */
  private drawHeadPlaceholder(
    centerX: number,
    topY: number,
    headWidth: number,
    headHeight: number
  ): void {
    if (!this.previewContainer) return;

    const placeholder = this.add.graphics();
    placeholder.fillStyle(COLORS.silhouetteFill, 0.8);
    placeholder.fillRoundedRect(
      centerX - headWidth / 2,
      topY,
      headWidth,
      headHeight,
      headWidth / 2
    );
    placeholder.lineStyle(2, COLORS.silhouetteOutline, 0.8);
    placeholder.strokeRoundedRect(
      centerX - headWidth / 2,
      topY,
      headWidth,
      headHeight,
      headWidth / 2
    );
    this.previewContainer.add(placeholder);

    const errorIcon = this.add
      .text(centerX, topY + headHeight / 2, '⚠️', {
        fontSize: '32px',
      })
      .setOrigin(0.5, 0.5);
    this.previewContainer.add(errorIcon);
  }

  // ─── Actions ──────────────────────────────────────────────────────────────────

  /**
   * Confirms the customization: saves the image to localStorage
   * and transitions to MapSelectScene.
   */
  private confirmCustomization(): void {
    if (!this.uploadedImageData) {
      this.showFallbackError();
      return;
    }

    try {
      // Save custom image via ProgressTracker if available, otherwise directly to localStorage
      if (this.progressTracker && typeof this.progressTracker.saveCharacterImage === 'function') {
        this.progressTracker.saveCharacterImage(this.uploadedImageData);
      } else {
        // Direct localStorage save as fallback
        localStorage.setItem('awe_custom_character', this.uploadedImageData);
      }

      // Transition to MapSelectScene
      this.transitionToMapSelect();
    } catch (err) {
      console.warn('[CustomizationScene] Save failed:', err);
      // Even if save fails, still transition — use default character
      this.transitionToMapSelect();
    }
  }

  /**
   * Skips customization and proceeds to MapSelectScene with default character.
   * Requirement 21.1 (skip option)
   */
  private skipCustomization(): void {
    this.transitionToMapSelect();
  }

  /**
   * Transitions to MapSelectScene with a fade effect.
   */
  private transitionToMapSelect(): void {
    this.cameras.main.fadeOut(300, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('MapSelectScene');
    });
  }

  // ─── UI Helpers ───────────────────────────────────────────────────────────────

  /**
   * Creates a styled button with text and click handler.
   */
  private createButton(
    x: number,
    y: number,
    label: string,
    color: number,
    onClick: () => void
  ): void {
    const container = this.mainContainer ?? this.previewContainer;
    if (!container) return;

    const { buttonWidth, buttonHeight, buttonRadius } = LAYOUT;

    // Button background
    const bg = this.add.graphics();
    bg.fillStyle(color, 1);
    bg.fillRoundedRect(
      x - buttonWidth / 2,
      y - buttonHeight / 2,
      buttonWidth,
      buttonHeight,
      buttonRadius
    );
    container.add(bg);

    // Button text
    const text = this.add
      .text(x, y, label, {
        fontFamily: 'Arial, sans-serif',
        fontSize: '20px',
        color: COLORS.text,
        fontStyle: 'bold',
        align: 'center',
      })
      .setOrigin(0.5, 0.5);
    container.add(text);

    // Interactive hit area
    const hitArea = this.add
      .rectangle(x, y, buttonWidth, buttonHeight, 0xffffff, 0)
      .setInteractive({ useHandCursor: true });
    container.add(hitArea);

    // Hover effect
    hitArea.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(COLORS.buttonHover, 1);
      bg.fillRoundedRect(
        x - buttonWidth / 2,
        y - buttonHeight / 2,
        buttonWidth,
        buttonHeight,
        buttonRadius
      );
    });

    hitArea.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(color, 1);
      bg.fillRoundedRect(
        x - buttonWidth / 2,
        y - buttonHeight / 2,
        buttonWidth,
        buttonHeight,
        buttonRadius
      );
    });

    // Click handler
    hitArea.on('pointerdown', () => {
      onClick();
    });
  }

  /**
   * Shows an error message on the main screen.
   */
  private showError(message: string): void {
    if (this.messageText) {
      this.messageText.setText(message);
      // Auto-clear after 5 seconds
      this.time.delayedCall(5000, () => {
        if (this.messageText) {
          this.messageText.setText('');
        }
      });
    }
  }

  /**
   * Shows the fallback error message when image processing fails.
   * Requirement 21.12
   */
  private showFallbackError(): void {
    this.showError('A imagem não funcionou. Vamos usar o personagem padrão!');
  }

  /**
   * Clears all UI containers.
   */
  private clearContainers(): void {
    if (this.mainContainer) {
      this.mainContainer.destroy(true);
      this.mainContainer = null;
    }
    if (this.previewContainer) {
      this.previewContainer.destroy(true);
      this.previewContainer = null;
    }
    this.messageText = null;
  }

  /**
   * Cleans up the hidden HTML file input element.
   */
  private cleanupFileInput(): void {
    if (this.fileInput) {
      if (this.fileInput.parentNode) {
        this.fileInput.parentNode.removeChild(this.fileInput);
      }
      this.fileInput = null;
    }
  }
}
